import { useEffect, useRef } from 'react';
import apiService from '../services/api';
import { DraftStateResponse } from '../types';

// Draft night is synchronous — a room of people watching one board. The
// boxscore polls at 30s because nobody is staring at it; here that would mean
// sitting a quarter of a minute wondering whether your turn arrived.
export const DRAFT_POLL_MS = 5000;

/**
 * Keeps the draft board current for everyone in the room.
 *
 * DraftPage previously fetched state once on mount and never again, so only
 * the person whose turn it was ever saw anything change — their own pick
 * response updated their screen, and every other screen stayed frozen at page
 * load. Three separate groups hit this: the ten-person league on Sept 20 (who
 * abandoned the draft room, hand-built rosters through free agency, then
 * deleted the league three minutes in), league 34, and league 17.
 *
 * Stops polling the moment the draft completes, and pauses while the tab is
 * hidden, matching useMatchupBoxscore.
 */
export function useDraftPolling(
  leagueId: number | undefined,
  isComplete: boolean | undefined,
  onUpdate: (state: DraftStateResponse) => void,
) {
  // Hold the callback in a ref so a new inline function on every render does
  // not tear down and rebuild the interval each time.
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  useEffect(() => {
    if (!leagueId || isComplete !== false) return undefined;

    let cancelled = false;
    const id = setInterval(async () => {
      if (document.hidden) return;
      try {
        const next = await apiService.getDraftState(leagueId);
        if (!cancelled) onUpdateRef.current(next);
      } catch {
        // A dropped poll is not worth surfacing — the next tick retries, and
        // an error banner mid-draft would be more alarming than useful.
      }
    }, DRAFT_POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [leagueId, isComplete]);
}
